import { Injectable, inject } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { map, Observable, of } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { PLAIN_HTTP_CLIENT } from '../tokens/http-tokens';

@Injectable({
  providedIn: 'root',
})
export class ConfigAwareTranslateLoader implements TranslateLoader {
  private http = inject(PLAIN_HTTP_CLIENT);
  private appConfigService = inject(AppConfigService);

  getTranslation(lang: string): Observable<any> {
    try {
      // Check if we have the languageISO1 key set
      let currentLanguageCode = localStorage.getItem('languageISO1');

      // If languageISO1 is not set, try to determine it from the requested lang parameter
      if (!currentLanguageCode) {
        // Check if lang parameter is a GUID (likely the languageGuid)
        if (lang === '24c9b00d-d028-4e04-a1aa-f04d2dcae2c3') {
          currentLanguageCode = 'en'; // Default English GUID maps to 'en'
        } else {
          // Check if lang is already an ISO1 code or a GUID key
          const availableLanguages = this.appConfigService.getAvailableLanguages();
          const availableKeys = this.appConfigService.getAvailableLanguageKeys();

          if (availableLanguages.includes(lang)) {
            // Lang is an ISO1 code
            currentLanguageCode = lang;
          } else if (availableKeys.includes(lang)) {
            // Lang is a GUID key, convert to ISO1
            currentLanguageCode = this.appConfigService.keyToIso1(lang) || 'en';
          } else {
            // Default to first available language, preferring 'en' if available
            currentLanguageCode = availableLanguages.includes('en') ? 'en' : availableLanguages[0] || 'en';
          }
        }
      }

      const localizationConfig = this.appConfigService.localizationConfig;

      if (currentLanguageCode && localizationConfig && localizationConfig.length > 0) {
        // Get URL directly using the new structure
        const configUrl = this.appConfigService.localizationUrl(currentLanguageCode);

        if (configUrl) {
          // URL already includes .json extension, just add cache busting
          const url = `${configUrl}?v=${new Date().getTime()}`;
          return this.http.get(url).pipe(
            map((res: any) => {
              const transformed = this.groupifyTranslations(res);
              return transformed;
            })
          );
        } else {
          // Try to suggest a fallback language if available
          const availableLanguages = this.appConfigService.getAvailableLanguages();
          if (availableLanguages.length > 0) {
            const fallbackLang = availableLanguages.includes('en') ? 'en' : availableLanguages[0];
            const fallbackUrl = this.appConfigService.localizationUrl(fallbackLang);
            if (fallbackUrl) {
              const url = `${fallbackUrl}?v=${new Date().getTime()}`;
              return this.http.get(url).pipe(
                map((res: any) => {
                  const transformed = this.groupifyTranslations(res);
                  return transformed;
                })
              );
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading translations from configuration:', error);
    }



    // Return an empty object if configuration is not available
    return of({});
  }

  groupifyTranslations(translations: any): any {
    const transformed: any = { ...translations }; // Keep all original keys

    // Find and transform primeng keys
    const primengKeys = Object.keys(translations).filter(key => key.startsWith('primeng.'));

    if (primengKeys.length > 0) {
      transformed.primeng = {};

      primengKeys.forEach(key => {
        // Extract nested key (e.g., "primeng.dayNames" -> "dayNames")
        const nestedKey = key.substring('primeng.'.length);
        let value = translations[key];

        // Parse JSON strings (for arrays and objects)
        if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
          try {
            value = JSON.parse(value);
          } catch (e) {
            console.warn(`Failed to parse primeng.${nestedKey}:`, e);
          }
        }

        transformed.primeng[nestedKey] = value;

        // Remove the flat key
        delete transformed[key];
      });
    }

    return transformed;
  }
}
